import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_ROUTES, API_DOMAIN, headers } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import * as listCache from '../../../../services/listCache';
import DatePicker from '../../../Reusable_Components/Template_Components/DatePicker';
import AsyncSelect from '../../../Reusable_Components/Template_Components/AsyncSelect';
import AsyncMultiSelect from '../../../Reusable_Components/Template_Components/AsyncMultiSelect';

// ── Constants ─────────────────────────────────────────────────────────────────

const STAGES = [
  { value: 'Prospecting',   label: 'Prospecting',  color: '#64748b', icon: '🔍' },
  { value: 'Qualification', label: 'Qualifying',   color: '#3b82f6', icon: '✔' },
  { value: 'Proposal',      label: 'Proposal',     color: '#8b5cf6', icon: '📄' },
  { value: 'Negotiation',   label: 'Negotiation',  color: '#f59e0b', icon: '🤝' },
  { value: 'Closed-Won',    label: 'Closed Won',   color: '#22c55e', icon: '🏆' },
  { value: 'Closed-Lost',   label: 'Closed Lost',  color: '#ef4444', icon: '✗' },
];

const DEAL_TYPES = ['New Business', 'Renewal', 'Upsell', 'Cross-sell', 'Partner Deal'];

const SOURCE_OPTIONS = [
  'Website', 'Referral', 'Cold Call', 'Email Campaign',
  'LinkedIn', 'Facebook / Instagram', 'Trade Show / Event',
  'Partner / Reseller', 'Walk-in', 'Other',
];

const PRIORITY = {
  Low:    { activeBg: '#f0fdf4', fg: '#15803d' },
  Medium: { activeBg: '#fffbeb', fg: '#a16207' },
  High:   { activeBg: '#fef2f2', fg: '#b91c1c' },
};

const probColor = (v) => {
  if (v >= 75) return '#22c55e';
  if (v >= 50) return '#3b82f6';
  if (v >= 25) return '#f59e0b';
  return '#ef4444';
};

const probLabel = (v) => {
  if (v >= 75) return 'High confidence — likely to close';
  if (v >= 50) return 'Moderate — actively progressing';
  if (v >= 25) return 'Low — needs more nurturing';
  return 'Very low — early stage';
};

const formatKES = (n) => {
  const num = Number(n);
  if (!num || isNaN(num)) return '—';
  return 'KES ' + num.toLocaleString('en-KE');
};

const EMPTY = {
  opportunity_name: '',
  value: '',
  stage: 'Qualification',
  probability_of_close: 50,
  assigned_to: [],
  related_lead: '',
  expected_close_date: '',
  deal_type: '',
  source: '',
  priority: 'Medium',
  contact_name: '',
  company: '',
  competitor: '',
  follow_up_date: '',
  next_steps: '',
  description: '',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const Field = ({ label, required, children, wide }) => (
  <div className={`form-field${wide ? ' span-full' : ''}`}>
    <label className="form-label">{label}{required && <span className="req"> *</span>}</label>
    {children}
  </div>
);

const SectionHeader = ({ icon, title, subtitle }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 20, paddingBottom: 14,
    borderBottom: '1px solid var(--border)',
  }}>
    <div style={{
      width: 36, height: 36, borderRadius: 8,
      background: 'var(--color-primary-pale)', color: 'var(--color-primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{subtitle}</div>}
    </div>
  </div>
);

const PrioritySelector = ({ value, onChange }) => (
  <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
    {['Low', 'Medium', 'High'].map((p, i) => {
      const active = value === p;
      const c = PRIORITY[p];
      return (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          style={{
            flex: 1, padding: '8px 0', fontSize: 12, fontWeight: 600,
            border: 'none', borderRight: i < 2 ? '1px solid var(--border)' : 'none',
            cursor: 'pointer', transition: 'all 0.13s',
            background: active ? c.activeBg : '#fff',
            color: active ? c.fg : 'var(--text-muted)',
          }}
        >
          {p}
        </button>
      );
    })}
  </div>
);

const StagePipeline = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
    {STAGES.map((s) => {
      const active = value === s.value;
      return (
        <button
          key={s.value}
          type="button"
          onClick={() => onChange(s.value)}
          style={{
            flex: '1 1 auto', minWidth: 90, padding: '7px 10px',
            borderRadius: 6,
            border: active ? `2px solid ${s.color}` : '2px solid var(--border)',
            background: active ? s.color + '18' : 'transparent',
            color: active ? s.color : 'var(--text-muted)',
            fontSize: 11, fontWeight: active ? 700 : 500,
            cursor: 'pointer', transition: 'all 0.14s',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
          }}
        >
          <span style={{ fontSize: 15 }}>{s.icon}</span>
          {s.label}
        </button>
      );
    })}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const OpportunityInput = () => {
  const { opportunityId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  const decodedId = opportunityId ? atob(opportunityId) : '';
  const id = decodedId.replace(/\D/g, '');
  const isEditing = !!id;

  const [form, setForm] = useState(EMPTY);
  const [original, setOriginal] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opportunityId) return;
    axios.get(`${API_DOMAIN}${API_ROUTES.opportunity}${id}/`, { headers })
      .then(res => { setForm(prev => ({ ...prev, ...res.data })); setOriginal(res.data); })
      .catch(err => console.error('Opportunity fetch error:', err));
  }, [opportunityId, id]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = isEditing
        ? `${API_DOMAIN}${API_ROUTES.opportunity}${id}/`
        : `${API_DOMAIN}${API_ROUTES.opportunity}`;
      const method = isEditing ? axios.put : axios.post;
      // follow_up_date is optional (nullable) — DRF's DateField rejects ''
      // outright ("wrong format"), so an empty value must be sent as null.
      // expected_close_date is required on this model, so it's left as-is:
      // the form already marks it required and an empty '' should fail loudly
      // here rather than silently becoming null and failing a different way.
      const payload = {
        ...form,
        follow_up_date: form.follow_up_date || null,
      };
      await method(url, payload, { headers });
      listCache.invalidate((u) => u.startsWith(`${API_DOMAIN}${API_ROUTES.opportunity}`));
      toast?.addToast({ type: 'success', message: isEditing ? 'Opportunity updated.' : 'Opportunity created.' });
      navigate('/opportunity/list');
    } catch (err) {
      const data = err?.response?.data;
      const msg = data
        ? typeof data === 'string' ? data
          : Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ')
        : 'Failed to save. Please check your inputs.';
      toast?.addToast({ type: 'error', message: msg, duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const prob = Number(form.probability_of_close) || 0;
  const dealValue = Number(form.value) || 0;
  const expectedRevenue = Math.round(dealValue * prob / 100);
  const currentStage = STAGES.find(s => s.value === form.stage) || STAGES[1];

  const getLeadLabel = (lead) => {
    if (lead.contact_data?.name) return lead.contact_data.name;
    if (lead.title) return lead.title;
    return `Lead #${lead.id}`;
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
            <Link to="/opportunity/list" style={{ color: 'var(--color-primary)' }}>Opportunities</Link>
            <span>›</span>
            <span>{isEditing ? 'Edit Opportunity' : 'New Opportunity'}</span>
          </div>
          <h1 className="page-title">{isEditing ? 'Edit Opportunity' : 'Create New Opportunity'}</h1>
          <p className="page-subtitle">Track and manage this deal through the sales pipeline</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/opportunity/list')}>
            Cancel
          </button>
        </div>
      </div>

      <form id="opp-form" onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>

          {/* ── Left column ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Section 1: Overview */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="💼" title="Opportunity Overview" subtitle="Name this deal and set its current pipeline stage" />
                <div className="form-grid" style={{ marginBottom: 20 }}>
                  <Field label="Opportunity Name / Deal Title" required wide>
                    <input
                      className="form-input"
                      placeholder="e.g. Acme Corp — Annual SaaS Subscription"
                      value={form.opportunity_name}
                      onChange={set('opportunity_name')}
                      required
                    />
                  </Field>
                  <Field label="Deal Type">
                    <select className="form-select" value={form.deal_type} onChange={set('deal_type')}>
                      <option value="">— Select type —</option>
                      {DEAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Priority">
                    <PrioritySelector value={form.priority} onChange={val => setForm(p => ({ ...p, priority: val }))} />
                  </Field>
                </div>
                <Field label="Pipeline Stage" required>
                  <StagePipeline
                    value={form.stage}
                    onChange={val => setForm(p => ({ ...p, stage: val }))}
                  />
                </Field>
              </div>
            </div>

            {/* Section 2: Financials */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="💰" title="Deal Financials" subtitle="Capture the monetary value and likelihood of closing" />
                <div className="form-grid">
                  <Field label="Deal Value (KES)" required>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="1000"
                      placeholder="0"
                      value={form.value}
                      onChange={set('value')}
                      required
                    />
                  </Field>
                  <Field label={`Win Probability — ${prob}%`}>
                    <div style={{ paddingTop: 4 }}>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={prob}
                        onChange={e => setForm(p => ({ ...p, probability_of_close: e.target.value }))}
                        style={{ width: '100%', accentColor: probColor(prob) }}
                      />
                      <div style={{
                        height: 6, borderRadius: 99, background: 'var(--border)',
                        overflow: 'hidden', marginTop: 6,
                      }}>
                        <div style={{
                          height: '100%', borderRadius: 99,
                          width: `${prob}%`,
                          background: probColor(prob),
                          transition: 'width 0.2s, background 0.2s',
                        }} />
                      </div>
                      <div style={{ fontSize: 11, color: probColor(prob), fontWeight: 600, marginTop: 6 }}>
                        {probLabel(prob)}
                      </div>
                    </div>
                  </Field>
                  <Field label="Source">
                    <select className="form-select" value={form.source} onChange={set('source')}>
                      <option value="">— Select source —</option>
                      {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Competitor (if known)">
                    <input
                      className="form-input"
                      placeholder="e.g. Salesforce, HubSpot"
                      value={form.competitor}
                      onChange={set('competitor')}
                    />
                  </Field>
                </div>
              </div>
            </div>

            {/* Section 3: Relationships */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="🔗" title="Relationships" subtitle="Link this opportunity to contacts, leads and accounts" />
                <div className="form-grid">
                  <div className="form-field">
                    <AsyncSelect
                      label="Related Lead"
                      endpoint={API_ROUTES.lead}
                      getOptionLabel={getLeadLabel}
                      value={form.related_lead}
                      onChange={set('related_lead')}
                      placeholder="Search for a lead…"
                    />
                  </div>
                  <Field label="Primary Contact Name">
                    <input
                      className="form-input"
                      placeholder="e.g. Jane Wanjiku"
                      value={form.contact_name}
                      onChange={set('contact_name')}
                    />
                  </Field>
                  <Field label="Company / Account">
                    <input
                      className="form-input"
                      placeholder="e.g. Acme Corporation"
                      value={form.company}
                      onChange={set('company')}
                    />
                  </Field>
                </div>
              </div>
            </div>

            {/* Section 4: Timeline */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="📅" title="Timeline" subtitle="Key dates to keep the deal on track" />
                <div className="form-grid">
                  <Field label="Expected Close Date" required>
                    <DatePicker
                      value={form.expected_close_date}
                      onChange={set('expected_close_date')}
                      placeholder="Select close date"
                      dateConstraint="future"
                      required
                    />
                  </Field>
                  <Field label="Next Follow-up Date">
                    <DatePicker
                      value={form.follow_up_date}
                      onChange={set('follow_up_date')}
                      placeholder="Select follow-up date"
                      dateConstraint="future"
                    />
                  </Field>
                </div>
              </div>
            </div>

            {/* Section 5: Assignment & Notes */}
            <div className="card">
              <div className="card-body">
                <SectionHeader icon="👥" title="Assignment &amp; Notes" subtitle="Who owns this deal and what happens next" />
                <div className="form-grid">
                  <div className="form-field span-full">
                    <AsyncMultiSelect
                      label="Assigned To"
                      endpoint={API_ROUTES.users}
                      getOptionLabel={(u) => u.username || u.email || `User ${u.id}`}
                      value={form.assigned_to || []}
                      onChange={set('assigned_to')}
                    />
                  </div>
                  <Field label="Next Steps" wide>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      placeholder="What needs to happen to advance this deal? e.g. Send proposal, schedule demo, get legal sign-off…"
                      value={form.next_steps}
                      onChange={set('next_steps')}
                    />
                  </Field>
                  <Field label="Description / Background" wide>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      placeholder="Context, history, or any notes about this opportunity…"
                      value={form.description}
                      onChange={set('description')}
                    />
                  </Field>
                </div>
              </div>
            </div>

          </div>

          {/* ── Right sidebar ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Stage indicator */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Current Stage</span>
              </div>
              <div className="card-body">
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 16px', borderRadius: 8,
                  background: currentStage.color + '15',
                  border: `1px solid ${currentStage.color}40`,
                }}>
                  <span style={{ fontSize: 20 }}>{currentStage.icon}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: currentStage.color }}>
                    {currentStage.label}
                  </span>
                </div>
                {form.priority && (
                  <div style={{
                    marginTop: 10, display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 12, color: 'var(--text-sub)',
                  }}>
                    Priority:
                    <span style={{
                      background: PRIORITY[form.priority]?.activeBg,
                      color: PRIORITY[form.priority]?.fg,
                      padding: '2px 10px', borderRadius: 99, fontWeight: 600, fontSize: 11,
                    }}>
                      {form.priority}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Deal summary */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Deal Summary</span>
              </div>
              <div className="card-body">
                {[
                  { label: 'Deal Value',       value: formatKES(form.value) },
                  { label: 'Win Probability',  value: `${prob}%` },
                  { label: 'Expected Revenue', value: expectedRevenue > 0 ? formatKES(expectedRevenue) : '—', highlight: true },
                ].map(({ label, value, highlight }) => (
                  <div key={label} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: label !== 'Expected Revenue' ? '1px solid var(--border)' : 'none',
                  }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</span>
                    <span style={{
                      fontSize: 13, fontWeight: highlight ? 700 : 600,
                      color: highlight ? 'var(--color-primary)' : 'var(--text-main)',
                    }}>{value}</span>
                  </div>
                ))}
                <div style={{
                  marginTop: 10, fontSize: 11, color: 'var(--text-muted)',
                  fontStyle: 'italic', textAlign: 'center',
                }}>
                  Expected = Value × Probability
                </div>
              </div>
            </div>

            {/* Win probability gauge */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Win Probability</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: probColor(prob), fontVariantNumeric: 'tabular-nums' }}>
                  {prob}%
                </span>
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                  <span>Unlikely</span>
                  <span>Certain</span>
                </div>
                <div style={{ height: 8, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 99,
                    width: `${prob}%`,
                    background: probColor(prob),
                    transition: 'width 0.2s, background 0.2s',
                  }} />
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Tips</span>
              </div>
              <div className="card-body">
                {[
                  { icon: '📞', text: 'Log all calls and meetings against this opportunity' },
                  { icon: '📄', text: 'Attach the proposal or contract under Documents' },
                  { icon: '🎯', text: 'Set a follow-up date so this deal never goes cold' },
                ].map(({ icon, text }, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-sub)',
                    marginBottom: i < 2 ? 10 : 0,
                  }}>
                    <span>{icon}</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* ── Form actions ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
          <button type="button" className="btn btn-danger" onClick={() => setForm(isEditing ? original : EMPTY)}>
            Reset
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ minWidth: 160 }}>
            {saving
              ? <><span className="spinner" style={{ width: 13, height: 13, borderWidth: 2 }} /> Saving…</>
              : isEditing ? '✓ Update Opportunity' : '+ Create Opportunity'
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default OpportunityInput;
